import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Shirt, Umbrella, Snowflake, Sun } from 'lucide-react';

const TravelPacking = ({ weatherData }) => {
  // Mock 3-day forecast data - in real implementation, this would come from the API
  const forecast = [
    { day: 'Today', high: 22, low: 12, precipitation: 0, layers: { day: 1, night: 2 } },
    { day: 'Tomorrow', high: 18, low: 8, precipitation: 20, layers: { day: 2, night: 3 } },
    { day: 'Day 3', high: 15, low: 5, precipitation: 60, layers: { day: 2, night: 3 } }
  ];

  const generatePackingList = () => {
    const items = new Set();
    
    // Base items based on max layers needed
    const maxLayers = Math.max(...forecast.map(f => Math.max(f.layers.day, f.layers.night)));
    
    if (maxLayers >= 1) items.add('T-shirts/base layers');
    if (maxLayers >= 2) items.add('Light jacket or overshirt');
    if (maxLayers >= 3) items.add('Warm sweater or fleece');
    if (maxLayers >= 4) items.add('Insulated jacket');
    if (maxLayers >= 5) items.add('Heavy winter coat');
    
    // Add weather-specific items
    const maxPrecip = Math.max(...forecast.map(f => f.precipitation));
    if (maxPrecip > 40) items.add('Rain jacket or umbrella');
    if (maxPrecip > 20) items.add('Water-resistant shoes');
    
    const minTemp = Math.min(...forecast.map(f => f.low));
    if (minTemp < 5) items.add('Warm accessories (gloves, hat)');
    if (minTemp < -5) items.add('Thermal underlayers');
    
    return Array.from(items);
  };

  const packingList = generatePackingList();

  return (
    <Card className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm border-white/20">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <span>✈️</span>
          Travel Packing Guide
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* 3-Day Forecast */}
        <div className="space-y-2">
          <h4 className="font-medium text-sm text-muted-foreground">3-Day Outlook</h4>
          <div className="grid gap-2">
            {forecast.map((day, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-primary/5 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="text-sm font-medium">{day.day}</div>
                  <div className="text-xs text-muted-foreground">
                    {day.high}°/{day.low}°C
                  </div>
                  {day.precipitation > 0 && (
                    <Badge variant="secondary" className="text-xs">
                      {day.precipitation}% rain
                    </Badge>
                  )}
                </div>
                <div className="text-xs text-muted-foreground">
                  {day.layers.day}-{day.layers.night} layers
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Packing Checklist */}
        <div className="space-y-2">
          <h4 className="font-medium text-sm text-muted-foreground">Recommended Items</h4>
          <div className="grid grid-cols-1 gap-2">
            {packingList.map((item, index) => (
              <div key={index} className="flex items-center gap-2 p-2 bg-white/50 dark:bg-gray-800/50 rounded">
                <div className="w-4 h-4 border border-gray-300 rounded-sm flex items-center justify-center">
                  <div className="w-2 h-2 bg-primary rounded-sm opacity-0 hover:opacity-100 transition-opacity"></div>
                </div>
                <span className="text-sm">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default TravelPacking;